import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail, Phone } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import StepProgress from "@/components/StepProgress";

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const bookingId = params.get("id") || "";

  return (
    <div data-testid="checkout-success-page" className="min-h-screen bg-ivory">
      <Navbar />

      {/* Midnight editorial header — matches Booking + Checkout */}
      <section
        data-testid="success-header"
        className="bg-midnight text-warm-white pt-32 md:pt-40 pb-16 md:pb-20 border-b border-white/10"
      >
        <div className="max-w-[92rem] mx-auto px-6 lg:px-12">
          <p className="sjc-eyebrow text-gold flex items-center gap-4">
            <span className="sjc-hairline w-10" />
            Booking Confirmed
          </p>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-[72px] leading-[1.02] tracking-[-0.015em] max-w-4xl">
            Your chauffeur <span className="italic font-light">is booked.</span>
          </h1>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16 md:py-24">
        <div data-testid="success-progress" className="mb-12 md:mb-16">
          <StepProgress current={5} />
        </div>

        <div className="bg-white border border-charcoal/10 rounded-none p-10 md:p-14 text-center">
          <div className="w-14 h-14 rounded-full border border-gold/40 text-gold grid place-items-center mx-auto">
            <CheckCircle2 className="w-7 h-7" strokeWidth={1.5} />
          </div>
          <h2 className="font-display mt-8 text-3xl md:text-4xl text-charcoal leading-tight tracking-tight">
            Thank you for choosing <span className="italic font-light">Sydney James.</span>
          </h2>
          <p className="mt-5 text-muted-light text-base leading-[1.75] font-light max-w-lg mx-auto">
            We&apos;ve received your booking and sent a confirmation email with the trip
            details. Our team will call or email you shortly to confirm.
          </p>

          {bookingId && (
            <div
              data-testid="checkout-success-ref"
              className="mt-8 inline-flex items-center gap-3 px-5 py-3 bg-ivory border border-gold/25 text-sm text-charcoal"
            >
              <span className="text-[10px] uppercase tracking-[0.22em] text-muted-light font-medium">
                Reference
              </span>
              <span className="font-mono text-charcoal text-xs md:text-sm">{bookingId}</span>
            </div>
          )}

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto">
            <a
              href="tel:+61400000000"
              data-testid="success-call-btn"
              className="inline-flex items-center justify-center gap-2.5 h-12 border border-charcoal/15 text-charcoal text-[11px] font-medium tracking-[0.22em] uppercase hover:border-midnight hover:bg-ivory transition-colors duration-300"
            >
              <Phone className="w-3.5 h-3.5 text-gold" strokeWidth={1.5} />
              Call Us
            </a>
            <a
              href="mailto:info@sydneyjames.com.au"
              data-testid="success-email-btn"
              className="inline-flex items-center justify-center gap-2.5 h-12 border border-charcoal/15 text-charcoal text-[11px] font-medium tracking-[0.22em] uppercase hover:border-midnight hover:bg-ivory transition-colors duration-300"
            >
              <Mail className="w-3.5 h-3.5 text-gold" strokeWidth={1.5} />
              Email
            </a>
          </div>

          <Link
            to="/"
            data-testid="checkout-success-home-btn"
            className="group mt-12 inline-flex items-center gap-3 text-muted-light hover:text-charcoal text-[11px] tracking-[0.28em] uppercase font-medium transition-colors duration-300"
          >
            <span className="w-8 h-px bg-gold transition-all duration-500 group-hover:w-12" />
            <span>Back to Home</span>
          </Link>
        </div>
      </main>

      <Footer />
      <FloatingActions />
    </div>
  );
}
